// The admin areas and the ACCESS LEVELS a grant of one carries.
//
// A grant is (email, area, access): the area is WHAT someone reaches, the level
// is HOW MUCH of it. Both string sets are the SHARED contract with the
// permissions backend (GET /api/admin/permissions/me → {areas:[{area,access}]},
// POST /api/admin/permissions {emails, areas, access}); the labels/paths/blurbs
// are UI-only.
//
// Keep both lists in lockstep with the backend's ADMIN_AREAS / ADMIN_ACCESS_LEVELS
// (backend/recruiter/models.py) and the admin_area_grants CHECK constraints.
// Additive only: a new area is a value here + a value there + a migration.
//
// There is no "viewer only" area. There was (migration 0119), and migration 0120
// replaced it with `read` on every area — one console-wide viewer role could
// only answer "may this person act" for the Explorer, while a level per grant
// answers it everywhere. Its holders were migrated to `explorer` at `read`.

export const AREAS = [
  // One place to land on a user or a project and see everything about it.
  // Ordered first because it is where an operator usually starts.
  "explorer",
  "customer_support",
  // The raised-flag worklist.
  "alerts",
  "talent_management",
  // No page of its own: it is the CAPABILITY to block and delete accounts,
  // and those buttons live on the Explorer's people views. See AREA_META.path.
  "user_administration",
  // The engineering surface: the agent debug dashboard. Last of the areas that
  // do product work, because its reader is an engineer rather than an operator.
  "developers",
  // The rollout-day TV dashboard. Standalone and narrow on purpose (migration
  // 0114): it implies nothing else and nothing else implies it, unlike the
  // broad-read roles above.
  "launch_day",
  // The project lifecycle funnel (migration 0115): aggregate conversion of
  // projects through the stage ladder. Aggregate-only by contract, so 0115
  // carries the grant over from `explorer` and `system_admin`; granting it
  // alone suits a stakeholder who should see numbers, not records.
  "funnel",
  // Version cohorts (migration 0131): the same metrics either side of a recorded
  // deploy, so a release can be judged on what it moved. Aggregate-only like
  // `funnel`, carried over from the same grants, and ordered next to it.
  "version_cohorts",
  // A/B tests (migration 0168): both arms of one experiment across the funnel
  // steps. It shipped inside `version_cohorts` and took its own area for
  // NAVIGATION rather than access — this list is what the sidebar renders, so a
  // page inside somebody else's area had no entry and no home card, and was
  // reachable only by typing its URL. The grant carries over from the area it
  // left, so nobody lost a page.
  "ab_tests",
  // (`bi_site` sat here until BI moved to its own repo, which administers its own
  // grants. Its comment outlived the entry; both are gone now, and the backend's
  // `0206` deleted the orphaned grant rows that were 500ing the permissions page.)
  // Administering the CONSOLE itself, one hierarchy level above the
  // product areas. Ordered last so the nav reads product-work-then-meta.
  "system_admin",
] as const;

export type Area = (typeof AREAS)[number];

/** How much of an area a grant carries. Ordered WEAKEST FIRST; `accessAllows` is
 *  the one comparison, so a level added later lands in one place rather than as
 *  a scattering of `=== "full"` checks that would silently refuse it. */
export const ACCESS_LEVELS = ["read", "full"] as const;

export type Access = (typeof ACCESS_LEVELS)[number];

/** How each level reads on the Permissions page. The blurb is what an admin is
 *  actually choosing between, so it names the CONSEQUENCE ("cannot change
 *  anything"), not the mechanism. */
export const ACCESS_META: Record<Access, { label: string; blurb: string }> = {
  read: {
    label: "Read only",
    blurb: "See everything in the area. Every control is hidden, and the server refuses it.",
  },
  full: {
    label: "Full access",
    blurb: "See the area and act in it.",
  },
};

/** The level a grant defaults to when nobody picks one. `full` matches the
 *  backend default, which is what every grant meant before levels existed. */
export const DEFAULT_ACCESS: Access = "full";

/** The levels that MEAN something for one area — the levels some route actually
 *  distinguishes. Not every area has both.
 *
 *  `web/deps/__init__.py` is the authority; this mirrors it, the same way `AREAS`
 *  mirrors `ADMIN_AREAS`, and `areas.test.ts` pins the map so the two cannot drift
 *  silently. Offering a level no route reads is not harmless: ticking User
 *  Administration at Read only writes a real grant row that unlocks NOTHING (its
 *  three routes are all writes, so `full` is its only half), and then shows up in
 *  the People table as "User Administration (read only)" — which reads like access
 *  that was conferred. The mirror case is quieter but still a lie: `funnel`,
 *  `launch_day`, `customer_support` and `developers` have no `full` half, so
 *  picking Full access on them reaches exactly the same screens.
 *
 *  When a route gains a half its area did not have, widen it HERE in the same PR
 *  as the dep, or the console will not offer the level the server now honours. */
const AREA_LEVELS: Record<Area, readonly Access[]> = {
  // Reads the project record (`read`); the four operator writes are `full`.
  explorer: ACCESS_LEVELS,
  // The support router is read-only BY DESIGN — a write there is a policy change,
  // not a gap to fill (web/routes/admin_support.py).
  customer_support: ["read"],
  // Watch the worklist (`read`) vs resolve a flag (`full`).
  alerts: ACCESS_LEVELS,
  // The BI dashboard and roster reads vs every write to the curated lists.
  talent_management: ACCESS_LEVELS,
  // Block and delete accounts. Every route on it is a write, so there is no read
  // half to grant — see `UserAdminDep`.
  user_administration: ["full"],
  // Gates the PAGE only; `/api/admin/debug/*` stays plain employee scope.
  developers: ["read"],
  // The dashboard is a read. Minting the TV's remote code is `system_admin`.
  launch_day: ["read"],
  // Aggregate-only by contract. Nothing writes through it.
  funnel: ["read"],
  // View past runs (`read`) vs trigger a new run (`full`, an LLM sweep that spends).
  // Aggregate-only, like `funnel`. Picking a release is a query parameter, not a
  // mutation, so nothing writes through it and there is no `full` half to offer.
  version_cohorts: ["read"],
  // Aggregate-only, like the two above. The experiment and the window are query
  // parameters, not mutations, so nothing writes through it.
  ab_tests: ["read"],
  // No `full` half: every BI route is a read (or a read-shaped enqueue), so
  // there is nothing a read-only holder needs to be refused.
  // Audit the roster and read the Red Button's card vs change either.
  system_admin: ACCESS_LEVELS,
};

/** The levels worth OFFERING for `area`. One entry means the choice is not a
 *  choice, and the picker should say what the grant confers instead of asking. */
export function levelsFor(area: Area): readonly Access[] {
  return AREA_LEVELS[area];
}

/** The level a NEWLY ticked area is granted at: Full access where that means
 *  something (what a grant meant before levels existed, so the common case stays
 *  one click), otherwise the area's only level.
 *
 *  Only for new ticks. An area someone ALREADY holds keeps the level it was
 *  granted at, even one this map no longer offers — a `funnel` grant carried to
 *  `full` by migration 0120 is left alone rather than quietly rewritten to `read`
 *  the next time somebody saves an unrelated change to that person. */
export function defaultAccessFor(area: Area): Access {
  const levels = levelsFor(area);
  return levels.includes(DEFAULT_ACCESS) ? DEFAULT_ACCESS : levels[0];
}

/** The levels a picker may OFFER right now — `levelsFor`, narrowed to Full access
 *  alone when the backend on the other end does not understand levels.
 *
 *  Without this the grant form over-grants during a deploy, silently and in the
 *  permissive direction: `GrantAreasRequest` predates `access` and sets no
 *  `model_config`, so an older server drops the key, stores FULL, and answers
 *  200. The admin picked Read only; the person got Full access; nothing said so.
 *  The two services deploy in parallel and the frontend usually lands first, so
 *  that window is the expected state for a few minutes on this very release.
 *
 *  Offering only what the server can honour is the whole guard: `Full access` is
 *  exactly what an old backend does with any grant, so what the form promises and
 *  what the row ends up holding agree again. Retires with `MyAccess.levelsSupported`. */
export function offeredLevels(area: Area, levelsSupported: boolean): readonly Access[] {
  return levelsSupported ? levelsFor(area) : [DEFAULT_ACCESS];
}

/** The level a newly ticked area takes, honouring the same constraint. */
export function newGrantLevel(area: Area, levelsSupported: boolean): Access {
  return levelsSupported ? defaultAccessFor(area) : DEFAULT_ACCESS;
}

/** One area someone holds, and how much of it. The unit the whole console reasons
 *  in: an area without its level cannot answer "may they act". */
export interface AreaAccess {
  area: Area;
  access: Access;
}

/** What `GET /api/admin/permissions/me` resolves to, once parsed. */
export type GrantedAreas = readonly AreaAccess[];

/** Does a HELD level satisfy a REQUIRED one? */
export function accessAllows(held: Access | null | undefined, needed: Access): boolean {
  if (!held) return false;
  return needed === "read" || held === "full";
}

/** Narrowing guard for a raw level string off the wire. */
export function isAccess(value: string): value is Access {
  return (ACCESS_LEVELS as readonly string[]).includes(value);
}

/** Narrow a raw level, defaulting to `read` for anything unrecognized.
 *
 *  Deliberately the WEAKER level, unlike the backend's `full` default on a grant
 *  WRITE: this parses what the server said someone holds, and reading an unknown
 *  value as full access would draw controls the server is about to refuse. A
 *  grant that omits the level entirely cannot happen — the contract requires it —
 *  so this only fires on drift, and drift should under-promise. */
export function parseAccess(raw: unknown): Access {
  return typeof raw === "string" && isAccess(raw) ? raw : "read";
}

export interface AreaMeta {
  /** Permissions-contract key. */
  key: Area;
  /** Sidebar / heading label. */
  label: string;
  /**
   * Route this area lives at, or `null` when the area is a pure CAPABILITY
   * with no page of its own.
   *
   * A null path means "this grant unlocks actions somewhere else", and the two
   * navigation surfaces (sidebar, home cards) skip it — linking to a page that
   * does not exist would render the not-found panel. It stays fully grantable
   * on the System Admin page, because the grant is the whole point of it.
   *
   * Two areas are capability-only:
   * - `user_administration` — its standalone users table was a second copy of
   *   the Explorer's people list, so the page went and its Block/Delete buttons
   *   moved onto the Explorer, shown only to holders.
   * - `customer_support` — its people list, person detail and conversation
   *   reader were likewise duplicates. The grant now unlocks the Explorer in
   *   SUPPORT mode: the same screens, reading the narrow talent-free payload,
   *   with no Sellers tab. See `unlockedBy` below.
   *
   * A read-only Explorer is NOT a third one of these. It used to be — the
   * `viewer_only` area (migration 0119) — and migration 0120 turned it into a
   * LEVEL on the `explorer` grant instead, because "read-only" is a question
   * every area has to answer and a tenth area could only ever answer it for one.
   */
  path: string | null;
  /** One-line description shown on the home cards. */
  blurb: string;
  /**
   * The grants that reveal this entry in the nav and on Home. Defaults to
   * `[key]`, and only differs where one screen serves more than one role.
   *
   * The Explorer is the case: `explorer` opens it in full mode and
   * `customer_support` opens it in support mode, but it must appear ONCE for
   * someone holding both — which is why nav visibility is a property of the
   * ENTRY, not a one-to-one mapping from grants to links.
   */
  unlockedBy: readonly Area[];
}

export const AREA_META: Record<Area, AreaMeta> = {
  explorer: {
    key: "explorer",
    label: "Explorer",
    path: "/explorer",
    blurb: "Search people and projects, then open one and see everything about it.",
    // Customer Support reaches the SAME screens in support mode. One entry, two
    // grants — and a read-only `explorer` grant is the same entry again, because
    // the LEVEL never changes which screens exist, only what they let you do.
    unlockedBy: ["explorer", "customer_support"],
  },
  alerts: {
    key: "alerts",
    label: "Alerts",
    path: "/alerts",
    blurb: "The raised-flag worklist: what needs a human, and who resolved it.",
    unlockedBy: ["alerts"],
  },
  customer_support: {
    key: "customer_support",
    label: "Customer Support",
    // Capability, not a place — see AreaMeta.path. It opens the Explorer in
    // support mode, so it must not also produce a nav entry of its own.
    path: null,
    blurb: "Read customer conversations, briefs, and files in the Explorer.",
    unlockedBy: ["customer_support"],
  },
  talent_management: {
    key: "talent_management",
    label: "Talent Management",
    path: "/talent-management",
    blurb: "Seller performance and marketplace health, from the analytics layer.",
    unlockedBy: ["talent_management"],
  },
  user_administration: {
    key: "user_administration",
    label: "User Administration",
    // Capability, not a place — see AreaMeta.path.
    path: null,
    blurb: "Block and delete accounts. The buttons appear on people in the Explorer.",
    unlockedBy: ["user_administration"],
  },
  developers: {
    key: "developers",
    label: "Developers",
    path: "/developers",
    blurb: "Agent debug: what each agent was given, what it answered, and why.",
    unlockedBy: ["developers"],
  },
  launch_day: {
    key: "launch_day",
    label: "Launch Day",
    path: "/launch",
    blurb: "The rollout TV dashboard: live aggregate signals, nothing else.",
    unlockedBy: ["launch_day"],
  },
  funnel: {
    key: "funnel",
    label: "Funnel",
    path: "/funnel",
    blurb: "Where projects flow and where they stop: stage conversion, budgets, timing.",
    unlockedBy: ["funnel"],
  },
  version_cohorts: {
    key: "version_cohorts",
    label: "Version Cohorts",
    path: "/version-cohorts",
    blurb: "Pick a deploy and read the same metrics either side of it.",
    unlockedBy: ["version_cohorts"],
  },
  ab_tests: {
    key: "ab_tests",
    // "Playground" rather than "A/B Tests" (renamed 2026-08-24). The area KEY,
    // the grant and the route stay `ab_tests` / `/ab-tests`: renaming those
    // would be a migration and a dead link for anyone holding the old URL, and
    // the label is the only part a reader sees.
    label: "Playground",
    path: "/ab-tests",
    blurb: "Both arms of an experiment, side by side across the funnel.",
    unlockedBy: ["ab_tests"],
  },
  system_admin: {
    key: "system_admin",
    label: "System Admin",
    path: "/system-admin",
    blurb: "Administer the console itself — who can access which area.",
    unlockedBy: ["system_admin"],
  },
};

/** Ordered metadata for EVERY area, including capability-only ones. Use this
 *  where the full set matters — chiefly the System Admin grant picker. */
export const AREA_LIST: readonly AreaMeta[] = AREAS.map((a) => AREA_META[a]);

/** An area that has a page to navigate to. */
export type NavigableAreaMeta = AreaMeta & { path: string };

/** The areas the sidebar and home cards may link to. Excludes capability-only
 *  areas (`path === null`), which have no page and would land on not-found. */
export const NAVIGABLE_AREAS: readonly NavigableAreaMeta[] = AREA_LIST.filter(
  (a): a is NavigableAreaMeta => a.path !== null,
);

/** The nav entries a holder of `granted` should see — each at most once, even
 *  when several of their grants unlock the same screen.
 *
 *  LEVEL-BLIND on purpose: a read-only grant still has somewhere to go. Hiding a
 *  nav entry from someone who may read the page would be a worse lie than showing
 *  it, and the page itself decides which controls to draw. */
export function visibleAreas(granted: GrantedAreas): readonly NavigableAreaMeta[] {
  const held = new Set(granted.map((g) => g.area));
  return NAVIGABLE_AREAS.filter((a) => a.unlockedBy.some((key) => held.has(key)));
}

/** Narrowing guard — the backend may (harmlessly) send an unknown area string. */
export function isArea(value: string): value is Area {
  return (AREAS as readonly string[]).includes(value);
}

/** Keep only recognized area keys from a raw server list (drops unknowns so a
 *  backend that adds an area ahead of the frontend degrades, never crashes). */
export function parseAreas(raw: readonly unknown[] | null | undefined): Area[] {
  if (!raw) return [];
  const seen = new Set<Area>();
  for (const v of raw) {
    if (typeof v === "string" && isArea(v)) seen.add(v);
  }
  return AREAS.filter((a) => seen.has(a));
}

/** Parse the wire's `[{area, access}]` into the console's granted set.
 *
 *  Unknown AREAS drop out (an ahead-of-us backend degrades rather than crashes),
 *  unknown LEVELS narrow to `read` (see `parseAccess` — drift must under-promise,
 *  never over-). Ordered by `AREAS` and deduped, so the granted set reads the same
 *  however the server ordered its rows.
 *
 *  A BARE STRING is also accepted, as that area at FULL access. That is the
 *  pre-0120 wire shape, and this is the other half of the deploy-skew problem:
 *  the backend keeps emitting the old `areas` field for one deploy so an old
 *  bundle does not go dark, and this accepts it so a NEW bundle running against a
 *  not-yet-deployed backend does not either. `full` is the honest reading of it —
 *  a server with no notion of levels only ever granted the one thing. It is the
 *  single case where an unrecognized shape resolves to the STRONGER level, and it
 *  is safe for the same reason the whole model is: the server refuses the write
 *  regardless of what this returns. Delete it with the backend's `areas` field. */
export function parseGrantedAreas(raw: readonly unknown[] | null | undefined): AreaAccess[] {
  if (!raw) return [];
  const held = new Map<Area, Access>();
  for (const entry of raw) {
    let area: unknown;
    let access: unknown;
    if (typeof entry === "string") {
      area = entry;
      access = "full";
    } else if (entry && typeof entry === "object") {
      ({ area, access } = entry as { area?: unknown; access?: unknown });
    } else {
      continue;
    }
    if (typeof area !== "string" || !isArea(area)) continue;
    const level = parseAccess(access);
    // A duplicate row keeps the STRONGER level: two grants of one area cannot
    // happen (the table keys on the pair), so this only fires on a malformed
    // payload, and there "they hold both" is the honest reading.
    if (held.get(area) !== "full") held.set(area, level);
  }
  return AREAS.filter((a) => held.has(a)).map((area) => ({
    area,
    access: held.get(area) as Access,
  }));
}

/** The level held on `area`, or null when it is not granted at all. */
export function accessOf(granted: GrantedAreas, area: Area): Access | null {
  return granted.find((g) => g.area === area)?.access ?? null;
}

/** Does this granted set include `area` AT ALL — at either level? Use it for
 *  anything that only READS. */
export function hasArea(granted: GrantedAreas, area: Area): boolean {
  return accessOf(granted, area) !== null;
}

/** Does this granted set include `area` at FULL access? Use it for every control
 *  that writes.
 *
 *  Hiding a control this refuses is manners; the server refuses it too (each
 *  area's write routes take the `full` level). Never point a control at a route a
 *  read-only holder may call to "make the page work" — the 403 is the permission,
 *  and a control that works because it was aimed elsewhere is the bug this whole
 *  split exists to prevent. */
export function canWrite(granted: GrantedAreas, area: Area): boolean {
  return accessAllows(accessOf(granted, area), "full");
}
